import React, { useEffect, useRef, useState, useCallback } from "react";
import adminAxios from "./adminAxios"; 
import { useAuth } from "../context/AuthContext"; 
import { 
  Send, 
  Paperclip, 
  Search, 
  Maximize2, 
  Activity, 
  User, 
  Clock,
  CheckCircle2,
  X,
  Edit2,
  Trash2,
  Type,
  Loader2,
  Pin,
  Archive,
  Ban,
  Bot,
  MoreVertical,
  Database,
  Inbox,
  AlertTriangle,
  ShieldAlert
} from "lucide-react";

const API_BASE = window.location.hostname === "localhost" 
  ? "https://golden-shaina-maritimesupplysuites-ed826a4c.koyeb.app" 
  : (process.env.REACT_APP_API_URL || "https://maritime-supply-suite-backend.onrender.com");

const THEME = {
  primary: "#0f172a", 
  accent: "#38bdf8",   
  success: "#10b981",
  danger: "#f43f5e",
  warning: "#f59e0b",
  bg: "#020617",      
  surface: "#1e293b",
  border: "rgba(255, 255, 255, 0.08)",
  textMain: "#f8fafc",
  textMuted: "#94a3b8"
};

export default function AdminChat() {
  const { socket } = useAuth();
  
  // Extract active tenant identifier from storage
  const tenantId = localStorage.getItem("adminId") || "global";

  const [conversations, setConversations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [header, setHeader] = useState(""); 
  const [editingMessageId, setEditingMessageId] = useState(null); 
  const [adminOnline, setAdminOnline] = useState(false);
  const [previewFileName, setPreviewFileName] = useState("");
  const [filePreview, setFilePreview] = useState(null); 
  const [fullImageOverlay, setFullImageOverlay] = useState(null); 
  const [isSending, setIsSending] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [viewArchived, setViewArchived] = useState(false);

  // --- MODAL STATE ---
  const [modalConfig, setModalConfig] = useState({ show: false, title: "", message: "", type: "info", onConfirm: null });

  const fileRef = useRef();
  const bottomRef = useRef();
  const inputRef = useRef();
  const audioRef = useRef(typeof Audio !== "undefined" ? new Audio("/notify.mp3") : null);

  const showAlert = (title, message, type = "info", onConfirm = null) => {
    setModalConfig({ show: true, title, message, type, onConfirm });
  };

  const closeAlert = () => setModalConfig({ ...modalConfig, show: false });

  const scrollBottom = useCallback((behavior = "smooth") => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior }), 100);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const resConv = await adminAxios.get("/api/chat/users", { params: { tenantId } });
      setConversations((resConv.data || []).sort((a, b) => new Date(b.lastTransmission) - new Date(a.lastTransmission)));
      const resUsers = await adminAxios.get("/api/admin/users", { params: { tenantId } });
      setAllUsers(resUsers.data || []);
      const resStatus = await adminAxios.get("/api/chat/admin/status", { params: { tenantId } });
      setAdminOnline(resStatus.data?.status === "online");
    } catch (e) {
      console.error("[System] Data Fetch Failure:", e.message);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- ACTIONS WITH PREMIUM POPUPS ---

  const toggleBot = async () => {
    if (!selectedUserId) return;
    const currentConvo = conversations.find(c => c._id === selectedUserId);
    const newStatus = !currentConvo?.botActive;
    try {
      await adminAxios.patch(`/api/chat/conversation/${selectedUserId}/toggle-bot`, { botActive: newStatus, tenantId });
      setConversations(prev => prev.map(c => c._id === selectedUserId ? { ...c, botActive: newStatus } : c));
    } catch (err) {
      showAlert("PROTOCOL ERROR", "Bot state synchronization failed.", "danger");
    }
  };

  const togglePin = async (userId) => {
    const target = conversations.find(c => c._id === userId);
    const isCurrentlyPinned = target?.pinned || false;
    const pinnedCount = conversations.filter(c => c.pinned).length;

    if (!isCurrentlyPinned && pinnedCount >= 5) {
      showAlert("CAPACITY REACHED", "Maximum 5 nodes can be prioritized at once.", "warning");
      return;
    }

    try {
      setConversations(prev => prev.map(c => c._id === userId ? { ...c, pinned: !isCurrentlyPinned } : c));
      await adminAxios.patch(`/api/chat/conversation/${userId}/pin`, { pinned: !isCurrentlyPinned, tenantId });
    } catch (err) {
      fetchData();
    }
  };

  const toggleArchive = async (userId) => {
    const target = conversations.find(c => c._id === userId);
    const isArchived = target?.archived || false;
    
    showAlert(
      isArchived ? "RESTORE NODE?" : "ARCHIVE NODE?",
      isArchived ? "This will return the node to active monitoring." : "Relocate this data stream to deep storage?",
      "info",
      async () => {
        try {
          await adminAxios.patch(`/api/chat/conversation/${userId}/archive`, { archived: !isArchived, tenantId });
          setConversations(prev => prev.map(c => c._id === userId ? { ...c, archived: !isArchived } : c));
          if (!isArchived) setSelectedUserId(null);
        } catch (err) {
          showAlert("ERROR", "Archive procedure failed.", "danger");
        }
      }
    );
  };

  const toggleBlock = async (userId) => {
    const target = conversations.find(c => c._id === userId) || allUsers.find(u => u._id === userId);
    const isBlocked = target?.isBlocked || false;
    
    showAlert(
      isBlocked ? "RESTORE SIGNAL?" : "TERMINATE SIGNAL?",
      isBlocked ? "Re-establish communication uplink with this node?" : "This will block all incoming transmissions from this sector.",
      isBlocked ? "success" : "danger",
      async () => {
        try {
          await adminAxios.post(`/api/chat/conversation/${userId}/block`, { tenantId });
          fetchData();
        } catch (e) { 
          showAlert("SECURITY BREACH", "Signal termination protocol failed.", "danger"); 
        }
      }
    );
  };

  const deleteFullChat = async (userId) => {
    showAlert(
      "PURGE ALL DATA?", 
      "Warning: This action will permanently erase the entire transmission history for this node.", 
      "danger", 
      async () => {
        try {
          await adminAxios.delete(`/api/chat/conversation/${userId}`, { data: { tenantId } });
          setConversations(prev => prev.filter(c => c._id !== userId));
          setSelectedUserId(null);
        } catch (e) { 
          showAlert("ERROR", "Data purge failed.", "danger"); 
        }
      }
    );
  };

  const deleteMsg = async (id) => {
    showAlert(
        "PURGE MESSAGE?", 
        "Remove this specific packet from the database?", 
        "danger", 
        async () => {
            try {
                await adminAxios.delete(`/api/chat/message/${id}`, { data: { tenantId } });
                setMessages(prev => prev.filter(m => m._id !== id));
            } catch (e) { showAlert("ERROR", "Purge failed.", "danger"); }
        }
    );
  };

  useEffect(() => {
    if (!socket) return;
    const joinAsAdmin = () => {
        socket.emit("join", { userId: "admin", role: "admin", tenantId });
        socket.emit("checkAdminStatus", { tenantId });
    };
    if (socket.connected) joinAsAdmin();
    socket.on("connect", joinAsAdmin);

    const handleNewMessage = (msg) => {
      if (msg.senderRole === "admin") return;
      const msgUserId = msg.user || msg.userId;
      const isCurrentChat = selectedUserId && String(msgUserId) === String(selectedUserId);

      if (isCurrentChat) {
        setMessages((prev) => {
          if (prev.find(m => m._id === msg._id)) return prev;
          return [...prev, msg].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        });
        scrollBottom();
        adminAxios.put(`/api/chat/seen/${selectedUserId}`, { tenantId }).catch(() => {});
      }

      setConversations((prev) => {
        const existingIndex = prev.findIndex(c => String(c._id) === String(msgUserId));
        if (existingIndex > -1) {
          const updated = [...prev];
          const conv = { ...updated[existingIndex] };
          conv.lastMessage = msg.text || (msg.file ? "📎 Attachment" : "");
          conv.lastTransmission = msg.createdAt;
          if (!isCurrentChat && msg.senderRole === "user") {
            conv.unreadCount = (Number(conv.unreadCount) || 0) + 1;
          }
          updated.splice(existingIndex, 1);
          return [conv, ...updated];
        } else {
          fetchData();
          return prev;
        }
      });

      if (msg.senderRole === "user" && !isCurrentChat) {
        audioRef.current?.play().catch(() => {});
      }
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("messageUpdated", (data) => {
        setMessages(prev => prev.map(m => m._id === data.messageId ? { ...m, text: data.text, header: data.header, isEdited: true } : m));
    });
    socket.on("messageDeleted", (data) => {
        setMessages(prev => prev.filter(m => m._id !== data.messageId));
        fetchData();
    });

    return () => {
      socket.off("connect", joinAsAdmin);
      socket.off("newMessage", handleNewMessage);
      socket.off("messageUpdated");
      socket.off("messageDeleted");
    };
  }, [socket, selectedUserId, fetchData, scrollBottom, tenantId]);

  const loadMessages = async (userId) => {
    try {
      setSelectedUserId(userId);
      setEditingMessageId(null);
      setText("");
      setHeader("");
      setShowMenu(false);
      const { data } = await adminAxios.get(`/api/chat/${userId}`, { params: { tenantId } });
      setMessages(Array.isArray(data) ? data.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) : []);
      await adminAxios.put(`/api/chat/seen/${userId}`, { tenantId });
      fetchData(); 
      scrollBottom("auto");
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (e) { console.error("Log Retrieval Error", e); }
  };

  const clearFile = () => {
    if (fileRef.current) fileRef.current.value = "";
    setPreviewFileName(""); 
    setFilePreview(null);
  };

  const sendMessage = async () => {
    const fileToUpload = fileRef.current?.files?.[0];
    if (!selectedUserId || (!text.trim() && !fileToUpload && !header.trim()) || isSending) return;
    setIsSending(true);
    try {
      if (editingMessageId) {
        await adminAxios.put(`/api/chat/message/${editingMessageId}`, { text, header, tenantId });
        setEditingMessageId(null);
      } else {
        const form = new FormData();
        form.append("userId", selectedUserId);
        form.append("text", text);
        form.append("header", header);
        form.append("senderRole", "admin");
        form.append("tenantId", tenantId);
        if (fileToUpload) form.append("file", fileToUpload);
        const { data: msg } = await adminAxios.post("/api/chat/send", form, {
          headers: { "x-tenant-id": tenantId }
        });
        setMessages((prev) => [...prev, msg]);
      }
      setText(""); setHeader(""); clearFile(); scrollBottom(); fetchData();
    } catch (e) { showAlert("TRANSMISSION FAILED", "Signal lost. Check network connection.", "danger"); } finally { setIsSending(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreviewFileName(file.name);
      if (file.type.startsWith("image/")) setFilePreview(URL.createObjectURL(file));
    }
  };

  const getFilePath = (path) => {
    if (!path) return null;
    if (path.startsWith("blob:") || path.startsWith("http")) return path;
    return `${API_BASE}/${path.replace(/\\/g, "/").replace(/^\/+/, "")}`;
  };

  const cleanSearch = search.toLowerCase().trim();
  const filteredRegistry = conversations.filter(c => 
    viewArchived ? c.archived === true : !c.archived
  ).filter(c => c.email?.toLowerCase().includes(cleanSearch));

  const pinnedChats = filteredRegistry.filter(c => c.pinned);
  const unpinnedChats = filteredRegistry.filter(c => !c.pinned);

  const globalFiltered = allUsers.filter(u => 
    u.email?.toLowerCase().includes(cleanSearch) && 
    !conversations.some(c => c._id === u._id)
  );

  const activeUser = conversations.find(c => c._id === selectedUserId) || allUsers.find(u => u._id === selectedUserId);

  return (
    <div style={styles.appContainer} data-tenant-scope={tenantId}>
      <div style={styles.contentWrapper}>
        <div style={styles.topBar}>
          <div style={styles.headerLeft}>
            <Activity color={THEME.accent} size={20} style={{marginRight: 12}} />
            <div>
              <h1 style={styles.title}>UPLINK COMMAND CENTER</h1>
              <p style={styles.subTitle}>NODE MONITORING & TECHNICAL DISPATCH</p>
            </div>
          </div>
          <div style={styles.adminStatusRow}>
            <div style={styles.statusIndicator(adminOnline)}>
              <div style={styles.pulseDot(adminOnline)}></div>
              <span style={styles.statusText}>{adminOnline ? "CENTRAL NODE ONLINE" : "CENTRAL NODE OFFLINE"}</span>
            </div>
            <button style={styles.toggleBtn} onClick={() => {
                const nextStatus = !adminOnline ? "online" : "offline";
                adminAxios.post("/api/chat/admin/status", { status: nextStatus, tenantId });
                setAdminOnline(!adminOnline);
            }}>TOGGLE SIGNAL</button>
          </div>
        </div>

        <div style={styles.chatLayout}>
          <div style={styles.userSidebar}>
            <div style={styles.searchContainer}>
              <div style={styles.searchInner}>
                <Search size={16} color={THEME.textMuted} />
                <input style={styles.searchField} placeholder={viewArchived ? "SEARCH ARCHIVE..." : "FILTER REGISTRY..."} value={search} onChange={(e) => setSearch(e.target.value)} />
                <div 
                    style={styles.archiveSwitch(viewArchived)} 
                    onClick={() => setViewArchived(!viewArchived)}
                    title={viewArchived ? "Show Active" : "Show Archived"}
                >
                    {viewArchived ? <Inbox size={16}/> : <Archive size={16}/>}
                </div>
              </div>
            </div>
            
            <div style={styles.userListScroll}>
              {pinnedChats.length > 0 && (
                <>
                  <div style={styles.sectionTitle}><Pin size={10} style={{marginRight:5}}/> PRIORITY NODES</div>
                  {pinnedChats.map((c) => (
                    <div key={c._id} style={styles.userCard(selectedUserId === c._id)} onClick={() => loadMessages(c._id)}>
                       <Pin size={12} style={styles.pinIconActive} onClick={(e) => { e.stopPropagation(); togglePin(c._id); }} />
                       <div style={styles.avatar}><User size={18}/></div>
                       <div style={styles.userCardBody}>
                         <div style={styles.userEmail}>{c.email} {c.isBlocked && <Ban size={10} color={THEME.danger}/>}</div>
                         <div style={styles.lastMsgText}>{c.lastMessage || "Priority Link"}</div>
                       </div>
                    </div>
                  ))}
                </>
              )}

              {unpinnedChats.length > 0 && (
                <>
                  <div style={styles.sectionTitle}>
                    {viewArchived ? <Archive size={10} style={{marginRight:5}}/> : <Clock size={10} style={{marginRight:5}}/>} 
                    {viewArchived ? "ARCHIVED NODES" : "ACTIVE CHANNELS"}
                  </div>
                  {unpinnedChats.map((c) => (
                    <div key={c._id} style={styles.userCard(selectedUserId === c._id)} onClick={() => loadMessages(c._id)}>
                      {!viewArchived && <Pin size={12} style={styles.pinIconHover} onClick={(e) => { e.stopPropagation(); togglePin(c._id); }} />}
                      <div style={styles.avatar}><User size={18}/></div>
                      <div style={styles.userCardBody}>
                        <div style={styles.userEmail}>{c.email} {c.isBlocked && <Ban size={10} color={THEME.danger}/>}</div>
                        <div style={styles.lastMsgText}>{c.lastMessage || "Awaiting input..."}</div>
                      </div>
                      <div style={styles.userCardMeta}>
                        <div style={styles.timeLabel}>{c.lastTransmission ? new Date(c.lastTransmission).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ""}</div>
                        {Number(c.unreadCount) > 0 && <div style={styles.unreadBadge}>{c.unreadCount}</div>}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {!viewArchived && globalFiltered.length > 0 && (
                <>
                  <div style={styles.sectionTitle}><Database size={10} style={{marginRight:5}}/> GLOBAL DIRECTORY</div>
                  {globalFiltered.map((u) => (
                    <div key={u._id} style={styles.userCard(selectedUserId === u._id)} onClick={() => loadMessages(u._id)}>
                      <div style={styles.avatar}><User size={18}/></div>
                      <div style={styles.userCardBody}>
                        <div style={styles.userEmail}>{u.email} {u.isBlocked && <Ban size={10} color={THEME.danger}/>}</div>
                        <div style={styles.lastMsgText}>New Connection</div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>

          <div style={styles.messageContainer}>
            {selectedUserId ? (
              <>
                <div style={styles.chatHeader}>
                  <div style={{display:'flex', gap: 12, alignItems:'center'}}>
                    <div style={styles.activeAvatar}>{activeUser?.email?.charAt(0).toUpperCase()}</div>
                    <div style={{display:'flex', flexDirection:'column'}}>
                        <div style={{display:'flex', alignItems:'center', gap: 8}}>
                            <span style={styles.chatHeaderUser}>{activeUser?.email}</span>
                            {activeUser?.isBlocked && <span style={styles.blockedTag}>BLOCKED</span>}
                        </div>
                        <span style={styles.chatHeaderSub}>BOT STATUS: {activeUser?.botActive ? "ACTIVE" : "PAUSED"}</span>
                    </div>
                  </div>
                  
                  <div style={{display: 'flex', gap: 15, alignItems: 'center'}}>
                    {!activeUser?.isBlocked && (
                        <button onClick={toggleBot} style={styles.botToggleBtn(activeUser?.botActive)}>
                          <Bot size={16} style={{marginRight: 8}}/>
                          {activeUser?.botActive ? "BOT ON" : "BOT OFF"}
                        </button>
                    )}

                    <div style={{position: 'relative'}}>
                        <MoreVertical size={20} style={{cursor:'pointer', color: THEME.textMuted}} onClick={() => setShowMenu(!showMenu)}/>
                        {showMenu && (
                            <div style={styles.commandMenu}>
                                <div style={styles.menuItem} onClick={() => { setShowMenu(false); toggleArchive(selectedUserId); }}>
                                    <Archive size={14}/> {activeUser?.archived ? "UNARCHIVE NODE" : "ARCHIVE CHAT"}
                                </div>
                                {!activeUser?.archived && (
                                    <div style={styles.menuItem} onClick={() => { setShowMenu(false); togglePin(selectedUserId); }}>
                                        <Pin size={14}/> {activeUser?.pinned ? "UNPIN NODE" : "PIN NODE"}
                                    </div>
                                )}
                                <div style={{...styles.menuItem, color: THEME.danger}} onClick={() => { setShowMenu(false); deleteFullChat(selectedUserId); }}>
                                    <Trash2 size={14}/> PURGE HISTORY
                                </div>
                                <div style={{...styles.menuItem, color: activeUser?.isBlocked ? THEME.success : THEME.danger, borderTop: `1px solid ${THEME.border}`}} onClick={() => { setShowMenu(false); toggleBlock(selectedUserId); }}>
                                    <Ban size={14}/> {activeUser?.isBlocked ? "UNBLOCK NODE" : "BLOCK NODE"}
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={styles.secureBadge}>
                      <CheckCircle2 size={12} style={{marginRight: 4}}/> SECURE
                    </div>
                  </div>
                </div>

                <div style={styles.messageList}>
                  {messages.map((m, i) => {
                    const isAdmin = m.senderRole === "admin";
                    const isStacked = i > 0 && messages[i - 1].senderRole === m.senderRole;
                    const fullPath = getFilePath(m.file);
                    return (
                      <div key={m._id || i} style={isAdmin ? styles.rowAdmin(isStacked) : styles.rowUser(isStacked)}>
                        <div style={isAdmin ? styles.bubbleAdmin : styles.bubbleUser}>
                          {m.header && <div style={styles.msgHeaderDisplay}>{m.header.toUpperCase()}</div>}
                          {m.text && <div style={{wordBreak: 'break-word', whiteSpace: 'pre-wrap'}}>{m.text}</div>}
                          {fullPath && (
                            <div style={{position: 'relative', marginTop: 10}}>
                              <img src={fullPath} style={styles.chatImg} onClick={() => setFullImageOverlay(fullPath)} alt="Asset" />
                              <div style={styles.imgOverlay} onClick={() => setFullImageOverlay(fullPath)}>
                                <Maximize2 size={16} color="white" />
                              </div>
                            </div>
                          )}
                          <div style={styles.metaRow}>
                              <div style={isAdmin ? styles.timeAdmin : styles.timeUser}>
                                {new Date(m.createdAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} 
                                {isAdmin && (m.seen ? <span style={{marginLeft: 5}}>✓✓</span> : <span style={{marginLeft: 5}}>✓</span>)}
                              </div>
                              {isAdmin && (
                                <div style={styles.adminActions}>
                                    <Edit2 size={12} onClick={() => { setEditingMessageId(m._id); setText(m.text || ""); setHeader(m.header || ""); }} style={styles.actionIcon} />
                                    <Trash2 size={12} onClick={() => deleteMsg(m._id)} style={styles.actionIcon} />
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                <div style={styles.inputArea}>
                  {activeUser?.isBlocked ? (
                      <div style={styles.blockedNotice}>
                          <Ban size={14} style={{marginRight: 8}}/> NODE BLOCKED. TRANSMISSIONS DISABLED.
                      </div>
                  ) : (
                    <>
                      {filePreview && (
                        <div style={styles.previewStrip}>
                          <div style={styles.previewInfo}>
                            <img src={filePreview} alt="Preview" style={styles.thumb} />
                            <span style={styles.fileNameText}>{previewFileName}</span>
                          </div>
                          <X size={16} onClick={clearFile} style={{cursor: 'pointer'}} />
                        </div>
                      )}
                      <div style={styles.inputContainer}>
                        <div style={styles.headerInputRow}>
                            <Type size={14} color={THEME.textMuted} style={{marginLeft: 15}}/>
                            <input style={styles.headerInput} placeholder="HEADER..." value={header} onChange={(e) => setHeader(e.target.value)} />
                        </div>
                        <div style={styles.inputFlex}>
                            <label style={styles.attachBtn}>
                              <Paperclip size={18} />
                              <input type="file" ref={fileRef} style={{ display: "none" }} onChange={handleFileChange} accept="image/*" />
                            </label>
                            <input ref={inputRef} style={styles.chatInput} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder="TYPE RESPONSE..." />
                            <button style={styles.sendBtn} onClick={sendMessage} disabled={isSending}>
                              {isSending ? <Loader2 size={18} className="spin"/> : <Send size={18} />}
                            </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div style={styles.emptyState}>
                 <Activity size={60} color={THEME.border}/>
                 <h3 style={styles.emptyTitle}>SYSTEM IDLE</h3>
                 <p style={{fontSize: '11px', opacity: 0.6}}>AWAITING UPLINK REQUEST</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- PREMIUM MODAL --- */}
      {modalConfig.show && (
        <div style={styles.modalOverlay}>
            <div style={styles.alertBox}>
                <div style={styles.alertHeader(modalConfig.type)}>
                    {modalConfig.type === 'danger' ? <ShieldAlert size={18}/> : <AlertTriangle size={18}/>}
                    <span>{modalConfig.title}</span>
                </div>
                <div style={styles.alertBody}>
                    {modalConfig.message}
                </div>
                <div style={styles.alertFooter}>
                    <button style={styles.cancelBtn} onClick={closeAlert}>ABORT</button>
                    {modalConfig.onConfirm && (
                        <button 
                            style={styles.confirmBtn(modalConfig.type)} 
                            onClick={() => { modalConfig.onConfirm(); closeAlert(); }}
                        >
                            CONFIRM
                        </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {fullImageOverlay && (
        <div style={styles.modalOverlay} onClick={() => setFullImageOverlay(null)}>
          <X size={32} color="white" style={styles.closeModal} onClick={() => setFullImageOverlay(null)} />
          <img src={fullImageOverlay} style={styles.modalImg} alt="Full" />
        </div>
      )}
      
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  appContainer: { display: "flex", height: "100vh", width: "100%", backgroundColor: "#020617", color: "#f8fafc", fontFamily: "'Inter', sans-serif" },
  contentWrapper: { flex: 1, display: "flex", flexDirection: "column" },
  topBar: { background: "#0f172a", padding: "15px 30px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid rgba(255,255,255,0.08)` },
  headerLeft: { display: "flex", alignItems: "center" },
  title: { margin: 0, fontSize: "14px", fontWeight: "900", letterSpacing: "2px" },
  subTitle: { margin: 0, fontSize: "9px", color: "#94a3b8", letterSpacing: "1px" },
  adminStatusRow: { display: "flex", alignItems: "center", gap: "20px" },
  statusIndicator: (online) => ({ display: "flex", alignItems: "center", gap: "10px", background: "rgba(0,0,0,0.3)", padding: "8px 16px", borderRadius: "6px", border: `1px solid rgba(255,255,255,0.08)` }),
  statusText: { fontSize: "10px", fontWeight: "800", letterSpacing: "0.5px" },
  pulseDot: (online) => ({ width: "8px", height: "8px", borderRadius: "50%", background: online ? "#10b981" : "#f43f5e", boxShadow: online ? `0 0 10px #10b981` : "none" }),
  toggleBtn: { background: "rgba(255,255,255,0.05)", color: "white", border: `1px solid rgba(255,255,255,0.08)`, padding: "8px 16px", borderRadius: "6px", fontWeight: "700", cursor: "pointer", fontSize: "10px" },
  chatLayout: { flex: 1, display: "flex", overflow: "hidden" },
  userSidebar: { width: "350px", background: "#0a0f1d", borderRight: `1px solid rgba(255,255,255,0.08)`, display: "flex", flexDirection: "column" },
  searchContainer: { padding: "20px" },
  searchInner: { display: "flex", alignItems: "center", background: "#1e293b", border: `1px solid rgba(255,255,255,0.08)`, borderRadius: "8px", padding: "0 12px" },
  searchField: { background: "transparent", border: "none", color: "white", padding: "12px", outline: "none", fontSize: "11px", width: "100%" },
  archiveSwitch: (active) => ({ cursor: 'pointer', color: active ? THEME.accent : THEME.textMuted, padding: '0 8px', transition: '0.2s' }),
  sectionTitle: { padding: "10px 20px", fontSize: "9px", fontWeight: "900", color: "#38bdf8", letterSpacing: "1px", display: "flex", alignItems: "center" },
  userListScroll: { flex: 1, overflowY: "auto" },
  userCard: (active) => ({ padding: "16px 20px", display: "flex", alignItems: "center", gap: "15px", cursor: "pointer", borderBottom: `1px solid rgba(255,255,255,0.08)`, background: active ? "rgba(56, 189, 248, 0.08)" : "transparent", position: 'relative' }),
  pinIconActive: { position: 'absolute', top: 5, right: 5, color: '#38bdf8' },
  pinIconHover: { position: 'absolute', top: 5, right: 5, color: 'rgba(255,255,255,0.1)', cursor: 'pointer' },
  avatar: { width: "40px", height: "40px", borderRadius: "8px", background: "#1e293b", display: "flex", alignItems: "center", justifyContent: "center", color: "#38bdf8" },
  userCardBody: { flex: 1, overflow: "hidden" },
  userEmail: { fontWeight: "700", fontSize: "12px", color: "white", marginBottom: "3px", display: 'flex', alignItems: 'center', gap: 6 },
  lastMsgText: { fontSize: "10px", color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  userCardMeta: { display: "flex", flexDirection: "column", alignItems: "flex-end" },
  timeLabel: { fontSize: "9px", color: "#94a3b8" },
  unreadBadge: { background: "#f43f5e", color: "#fff", fontSize: "10px", width: "18px", height: "18px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" },
  messageContainer: { flex: 1, display: "flex", flexDirection: "column", background: "#020617" },
  chatHeader: { padding: "15px 30px", background: "#0f172a", borderBottom: `1px solid rgba(255,255,255,0.08)`, display: "flex", justifyContent: "space-between", alignItems: "center" },
  blockedTag: { background: "#f43f5e", color: "white", fontSize: "8px", fontWeight: "900", padding: "2px 6px", borderRadius: "4px" },
  botToggleBtn: (active) => ({ background: active ? "rgba(16, 185, 129, 0.15)" : "rgba(244, 63, 94, 0.15)", color: active ? "#10b981" : "#f43f5e", border: `1px solid ${active ? "#10b981" : "#f43f5e"}`, padding: "6px 12px", borderRadius: "6px", fontSize: "10px", fontWeight: "900", display: 'flex', alignItems: 'center', cursor: 'pointer' }),
  commandMenu: { position: 'absolute', top: 30, right: 0, background: '#1e293b', border: `1px solid rgba(255,255,255,0.1)`, borderRadius: '8px', width: '180px', zIndex: 100, boxShadow: '0 10px 25px rgba(0,0,0,0.5)' },
  menuItem: { padding: '12px 15px', fontSize: '10px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', transition: '0.2s' },
  activeAvatar: { width: 36, height: 36, borderRadius: '50%', background: "#38bdf8", color: "#020617", display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900' },
  chatHeaderUser: { fontWeight: "800", color: "white", fontSize: "14px" },
  chatHeaderSub: { fontSize: "9px", color: "#94a3b8" },
  secureBadge: { display: 'flex', alignItems: 'center', fontSize: "9px", color: "#10b981", fontWeight: "900", background: "rgba(16, 185, 129, 0.1)", padding: "5px 10px", borderRadius: "4px" },
  messageList: { flex: 1, overflowY: "auto", padding: "30px", display: "flex", flexDirection: "column" },
  rowAdmin: (stack) => ({ alignSelf: "flex-end", marginBottom: stack ? "4px" : "20px", maxWidth: "70%" }),
  rowUser: (stack) => ({ alignSelf: "flex-start", marginBottom: stack ? "4px" : "20px", maxWidth: "70%" }),
  bubbleAdmin: { background: "#38bdf8", color: "#020617", padding: "14px 18px", borderRadius: "18px 18px 4px 18px", fontSize: "13px", fontWeight: "600" },
  bubbleUser: { background: "#1e293b", color: "#f8fafc", padding: "14px 18px", borderRadius: "18px 18px 18px 4px", fontSize: "13px", border: `1px solid rgba(255,255,255,0.08)` },
  msgHeaderDisplay: { fontSize: '10px', fontWeight: '900', marginBottom: '8px', borderBottom: '1px solid rgba(0,0,0,0.1)' },
  metaRow: { display: 'flex', justifyContent: 'space-between', marginTop: 6 },
  adminActions: { display: 'flex', gap: 10 },
  actionIcon: { cursor: 'pointer', opacity: 0.6 },
  timeAdmin: { fontSize: "9px", fontWeight: "800" },
  timeUser: { fontSize: "9px", opacity: 0.6 },
  chatImg: { width: "100%", borderRadius: "8px", cursor: 'pointer' },
  imgOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', opacity: 0, transition: '0.2s', cursor: 'pointer', ':hover': { opacity: 1 } },
  inputArea: { padding: "20px 30px", background: "#0f172a" },
  blockedNotice: { background: "rgba(244, 63, 94, 0.1)", color: "#f43f5e", padding: "15px", borderRadius: "8px", textAlign: "center", fontSize: "11px", fontWeight: "900", letterSpacing: "1px", border: "1px solid rgba(244, 63, 94, 0.2)" },
  
  // MODAL STYLES
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(2, 6, 23, 0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" },
  alertBox: { background: "#0f172a", border: `1px solid ${THEME.border}`, borderRadius: "12px", width: "400px", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.5)" },
  alertHeader: (type) => ({ padding: "15px 20px", background: "rgba(255,255,255,0.03)", borderBottom: `1px solid ${THEME.border}`, color: type === 'danger' ? THEME.danger : (type === 'success' ? THEME.success : THEME.accent), fontSize: "11px", fontWeight: "900", letterSpacing: "1px", display: "flex", alignItems: "center", gap: "10px" }),
  alertBody: { padding: "25px 20px", fontSize: "13px", color: THEME.textMuted, lineHeight: "1.6", textAlign: "center" },
  alertFooter: { padding: "15px 20px", display: "flex", gap: "12px", justifyContent: "center", background: "rgba(0,0,0,0.2)" },
  cancelBtn: { flex: 1, background: "transparent", border: `1px solid ${THEME.border}`, color: "white", padding: "10px", borderRadius: "6px", fontSize: "10px", fontWeight: "800", cursor: "pointer" },
  confirmBtn: (type) => ({ flex: 1, background: type === 'danger' ? THEME.danger : THEME.accent, border: "none", color: type === 'danger' ? "white" : "#020617", padding: "10px", borderRadius: "6px", fontSize: "10px", fontWeight: "900", cursor: "pointer" }),
  
  closeModal: { position: 'absolute', top: 30, right: 30, cursor: 'pointer', zIndex: 1001 },
  modalImg: { maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', boxShadow: '0 0 50px rgba(0,0,0,0.8)' },
  inputContainer: { background: "#1e293b", borderRadius: "12px", border: `1px solid ${THEME.border}`, overflow: "hidden" },
  headerInputRow: { display: "flex", alignItems: "center", borderBottom: `1px solid ${THEME.border}`, background: "rgba(0,0,0,0.1)" },
  headerInput: { background: "transparent", border: "none", color: THEME.accent, padding: "10px 15px", fontSize: "10px", fontWeight: "900", flex: 1, outline: "none" },
  inputFlex: { display: "flex", alignItems: "center", padding: "5px" },
  attachBtn: { padding: "10px", color: "#94a3b8", cursor: "pointer" },
  chatInput: { flex: 1, background: "transparent", border: "none", color: "white", padding: "12px", outline: "none", fontSize: "13px" },
  sendBtn: { background: "#38bdf8", color: "#020617", border: "none", width: "40px", height: "40px", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  previewStrip: { background: "#1e293b", padding: "8px 12px", borderRadius: "8px", marginBottom: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  previewInfo: { display: "flex", alignItems: "center", gap: "10px" },
  thumb: { width: "30px", height: "30px", borderRadius: "4px", objectFit: "cover" },
  fileNameText: { fontSize: "10px", color: "#94a3b8" },
  emptyState: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", opacity: 0.5 },
  emptyTitle: { fontSize: "14px", fontWeight: "900", letterSpacing: "3px", margin: "20px 0 5px" }
};